// PounceQualifyingQuestions.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Pounce agent creation flow: Step 5 ke baad aur Plan/Payment se THEEK pehle
// aane wali screen.
// Yahan user standard 4 qualifying questions ko checkbox se select/unselect kar
// sakta hai, edit kar sakta hai, aur apna custom question add kar sakta hai.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from "react";
import styles from "./PounceQualifyingQuestions.module.css";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import HeaderBar from "../HeaderBar/HeaderBar";
import { suggestPounceVariable } from "../../Store/apiStore";

export const DEFAULT_POUNCE_QUALIFYING_QUESTIONS = [
  {
    id: "q1",
    question: "Could you briefly tell me what specific service or project you need help with?",
    type: "free_text",
    variable: "service_needed",
    isDisqualifier: false,
    checked: true,
  },
  {
    id: "q2",
    question: "How soon are you looking to get this started — right away, or within the next couple of weeks?",
    type: "multiple_choice",
    options: ["Immediately", "Within 1-2 weeks", "Within a month", "Just exploring"],
    variable: "start_timeline",
    isDisqualifier: false,
    checked: true,
  },
  {
    id: "q3",
    question: "Are you the primary decision-maker for this project?",
    type: "yes_no",
    variable: "decision_maker",
    isDisqualifier: false,
    checked: true,
  },
  {
    id: "q4",
    question: "Would you like to lock in a quick consultation on our calendar so our specialist can provide an accurate quote?",
    type: "yes_no",
    variable: "wants_consultation",
    isDisqualifier: true,
    disqualifyAnswer: "no",
    checked: true,
  },
];

// Ye naam call ke analysis me pehle se bane hain — inhe variable banane par
// backend nayi field nahi banata, to jawab alag se capture hi nahi hota.
const RESERVED_VARIABLES = new Set([
  "name", "email", "phone", "phone_number", "address", "reason", "lead_type", "urgency",
  "message_taken", "message_text", "call_summary", "user_sentiment", "pounce_outcome",
  "is_qualified", "qualification_summary", "service_requested", "project_timeline",
  "is_decision_maker", "opt_out_detected", "appointment_booked", "appointment_date",
  "appointment_time", "appointment_timezone",
]);

// Type rukne ke itni der baad naam maanga jaata hai — har akshar pe API nahi.
const SUGGEST_DEBOUNCE_MS = 700;
// Itne se chhote text ka naam banana bekaar ("wh", "what").
const SUGGEST_MIN_CHARS = 8;

// AI wala chamakta sitara (✦) — loader aur "AI ne banaya" nishaan, dono me.
const SparkleIcon = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l1.9 5.6a3 3 0 0 0 1.9 1.9L21.4 11.4a.6.6 0 0 1 0 1.2l-5.6 1.9a3 3 0 0 0-1.9 1.9L12 22l-1.9-5.6a3 3 0 0 0-1.9-1.9l-5.6-1.9a.6.6 0 0 1 0-1.2l5.6-1.9a3 3 0 0 0 1.9-1.9L12 2Z" />
    <path d="M19 2l.7 2.1a1 1 0 0 0 .7.7L22.5 5.5l-2.1.7a1 1 0 0 0-.7.7L19 9l-.7-2.1a1 1 0 0 0-.7-.7L15.5 5.5l2.1-.7a1 1 0 0 0 .7-.7L19 2Z" opacity=".7" />
  </svg>
);

const cleanVariable = (v) => String(v || "").replace(/_+$/, "");

/** Chune hue sawaalon ke variable ki galtiyan: { [questionId]: message }. */
const variableErrors = (questions) => {
  const errors = {};
  const seen = new Map();
  for (const q of questions) {
    if (q.checked === false) continue;
    const name = cleanVariable(q.variable);
    if (!name) errors[q.id] = "Add a name so this answer can be saved.";
    else if (!/^[a-z]/.test(name)) errors[q.id] = "Start the name with a letter.";
    else if (RESERVED_VARIABLES.has(name)) errors[q.id] = "This name is already in use. Try a different one.";
    else if (seen.has(name)) errors[q.id] = "Another question already uses this name.";
    else seen.set(name, q.id);
  }
  return errors;
};

const PounceQualifyingQuestions = ({
  initialQuestions = null,
  onBack,
  onContinue,
  busy = false,
}) => {
  const [questions, setQuestions] = useState(() => {
    if (Array.isArray(initialQuestions) && initialQuestions.length > 0) {
      return initialQuestions.map((q) => ({
        ...q,
        checked: q.checked !== undefined ? q.checked : true,
      }));
    }
    return DEFAULT_POUNCE_QUALIFYING_QUESTIONS;
  });

  // Variable ka naam sawaal se apne aap banta hai (backend → Gemini). Kis sawaal
  // ke liye abhi naam ban raha hai:
  const [suggesting, setSuggesting] = useState({});
  // Har sawaal ka wo text jiske liye naam maanga ja chuka — wahi text dobara nahi.
  const suggestedFor = useRef({});
  // Har sawaal ka chalta hua debounce timer.
  const suggestTimers = useRef({});
  const questionsRef = useRef(questions);
  questionsRef.current = questions;

  /**
   * Sawaal ke text se variable banao. User ne naam khud likha ho
   * (variableManual) to use nahi chhedte. `force` = text wahi ho tab bhi.
   */
  const suggestVariable = async (id, { force = false } = {}) => {
    const q = questionsRef.current.find((x) => x.id === id);
    const text = q?.question?.trim();
    if (!q || !text || q.variableManual) return;
    if (!force && suggestedFor.current[id] === text) return;
    suggestedFor.current[id] = text;

    setSuggesting((m) => ({ ...m, [id]: true }));
    try {
      const taken = questionsRef.current
        .filter((x) => x.id !== id && x.checked !== false && x.variable)
        .map((x) => cleanVariable(x.variable));
      const variable = await suggestPounceVariable(text, taken);
      // Jawab aane tak user ne naam khud likh diya ya sawaal phir badal diya → chhod do.
      setQuestions((prev) =>
        prev.map((x) =>
          x.id === id && !x.variableManual && x.question?.trim() === text && variable ? { ...x, variable } : x
        )
      );
    } catch (_) {
      // Naam nahi bana — field khaali rahegi aur user khud likh sakta hai.
      delete suggestedFor.current[id];
    } finally {
      setSuggesting((m) => ({ ...m, [id]: false }));
    }
  };

  useEffect(() => {
    const timers = suggestTimers.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  // Khulte hi: jin sawaalon pe naam nahi hai (purana saved draft) unke liye banao.
  // Jin pe pehle se hai unka text "ho chuka" maan lo, taaki bina badle dobara na bane.
  useEffect(() => {
    for (const q of questionsRef.current) {
      if (q.variable) suggestedFor.current[q.id] = q.question?.trim();
      else if (q.checked !== false) suggestVariable(q.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Continue dabane ke baad hi galtiyan dikhti hain — khulte hi laal nahi.
  const [showErrors, setShowErrors] = useState(false);
  const errors = variableErrors(questions);

  // State for Add Custom Question form
  const [showAddForm, setShowAddForm] = useState(false);
  const [customText, setCustomText] = useState("");
  const addFormRef = useRef(null);
  const bottomAnchorRef = useRef(null);

  // Auto-scroll when "Add Custom Question" form opens
  useEffect(() => {
    if (showAddForm) {
      setTimeout(() => {
        addFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    }
  }, [showAddForm]);

  // Toggle checkbox on a question
  const handleToggleCheck = (id) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, checked: !q.checked } : q))
    );
  };

  // Edit question text
  const handleQuestionChange = (id, newText) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, question: newText } : q))
    );
    // Type karte-karte: ruk kar (debounce) naam banwao. Har naye akshar pe
    // pichhla timer radd, to API tabhi jaati hai jab user thahar jaye.
    clearTimeout(suggestTimers.current[id]);
    if (newText.trim().length < SUGGEST_MIN_CHARS) return;
    suggestTimers.current[id] = setTimeout(() => suggestVariable(id), SUGGEST_DEBOUNCE_MS);
  };

  // Sawaal ka "variable": call ke baad is sawaal ka jawab isi naam se save
  // hota hai (data capture) — phir Integrations me CRM ke column se joda ja
  // sakta hai. Naam chhote akshar / ank / underscore me.
  const handleVariableChange = (id, raw) => {
    const variable = raw
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+/, "")
      .slice(0, 40);
    // Khaali kar diya = "phir se apne aap banao"; kuch likha = user ka apna naam.
    setQuestions((prev) => prev.map((q) => (q.id === id ? { ...q, variable, variableManual: !!variable } : q)));
  };

  // Delete custom question
  const handleDeleteCustom = (id, e) => {
    e.stopPropagation();
    setQuestions((prev) => prev.filter((q) => q.id !== id));
  };

  // Add custom question handler
  const handleAddCustomQuestion = () => {
    if (!customText.trim()) return;

    const newQ = {
      id: `custom_${Date.now()}`,
      question: customText.trim(),
      type: "free_text",
      isDisqualifier: false,
      isCustom: true,
      checked: true,
    };

    setQuestions((prev) => [...prev, newQ]);
    setCustomText("");
    setShowAddForm(false);
    // State lagne ke baad (questionsRef taaza ho) is sawaal ka naam banwao.
    setTimeout(() => suggestVariable(newQ.id), 0);

    // Auto-scroll to bottom so the new question is completely visible above the footer
    setTimeout(() => {
      bottomAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 80);
  };

  // Submit only checked questions
  const handleNext = () => {
    // Har chune hue sawaal ka variable zaroori hai — isi naam se uska jawab
    // call ke baad save hota hai.
    const firstBad = Object.keys(errors)[0];
    if (firstBad) {
      setShowErrors(true);
      document.getElementById(`pqq-var-${firstBad}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const selected = questions
      .filter((q) => q.checked !== false)
      .map((q) => ({
        id: q.id,
        question: q.question?.trim() || "",
        type: q.type || "free_text",
        ...(Array.isArray(q.options) ? { options: q.options } : {}),
        isDisqualifier: !!q.isDisqualifier,
        ...(q.disqualifyAnswer ? { disqualifyAnswer: q.disqualifyAnswer } : {}),
        variable: cleanVariable(q.variable),
      }));

    onContinue?.(selected);
  };

  const selectedCount = questions.filter((q) => q.checked !== false).length;

  return (
    <div className={styles.page}>
      {/* Baaki Outbound pages jaisa common HeaderBar: back arrow, bada title,
          neeche chhota "Outbound Caller" (h6 ko neeche .header h6 rakhta hai). */}
      <div className={styles.header}>
        <HeaderBar
          title="Qualifying Questions"
          subtitle="Outbound Caller"
          backgroundColor="#fff"
          onBack={onBack}
        />
      </div>
      <p className={styles.trial}>
        Select or customize the questions Outbound Caller will ask to qualify your leads before booking.
      </p>

      <div className={styles.topBar}>
        <h2 className={styles.sectionTitle}>Questions</h2>
        <span className={styles.countPill}>
          {selectedCount}/{questions.length} selected
        </span>
      </div>

      <div className={styles.questionsList}>
        {questions.map((q, idx) => {
          const isChecked = q.checked !== false;
          return (
            <div
              key={q.id || idx}
              className={`${styles.questionCard} ${
                isChecked ? styles.questionCardActive : styles.questionCardUnchecked
              }`}
            >
              <div className={styles.cardHeader}>
                {/* Button + role="checkbox": pehle ye sirf onClick wala div tha,
                    keyboard se select/unselect hota hi nahi tha. */}
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={isChecked}
                  className={styles.cardLeftHeader}
                  onClick={() => handleToggleCheck(q.id)}
                >
                  <span
                    className={`${styles.checkboxBox} ${
                      isChecked ? styles.checkboxBoxChecked : ""
                    }`}
                  >
                    {isChecked && (
                      <svg className={styles.checkIcon} viewBox="0 0 24 24" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  <span className={styles.badgeGroup}>
                    <span className={styles.qIndexBadge}>Question {idx + 1}</span>
                    {q.isCustom && <span className={styles.customBadge}>Custom</span>}
                  </span>
                </button>

                {q.isCustom && (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={(e) => handleDeleteCustom(q.id, e)}
                    title="Delete custom question"
                    aria-label="Delete custom question"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <textarea
                className={styles.questionInput}
                rows={2}
                value={q.question}
                onChange={(e) => handleQuestionChange(q.id, e.target.value)}
                onBlur={() => {
                  clearTimeout(suggestTimers.current[q.id]);
                  suggestVariable(q.id);
                }}
                placeholder="Enter question text..."
              />

              {/* Variable = is sawaal ka jawab kis naam se save ho. */}
              <label className={styles.variableRow}>
                <span className={styles.variableLabel}>
                  Save answer as <span className={styles.variableRequired} aria-hidden="true">*</span>
                </span>
                {/* Wrapper: AI ka loader input ke upar baithta hai; naam ban jaane
                    par daayein kone me chhota "AI ne banaya" sitara. */}
                <span className={`${styles.variableField} ${suggesting[q.id] ? styles.variableFieldBusy : ""}`}>
                <input
                  type="text"
                  id={`pqq-var-${q.id}`}
                  className={`${styles.variableInput} ${
                    showErrors && isChecked && errors[q.id] ? styles.variableInputError : ""
                  } ${suggesting[q.id] ? styles.variableInputBusy : ""}`}
                  aria-busy={!!suggesting[q.id]}
                  required={isChecked}
                  aria-invalid={showErrors && isChecked && !!errors[q.id]}
                  value={q.variable || ""}
                  onChange={(e) => handleVariableChange(q.id, e.target.value)}
                  onBlur={() => !cleanVariable(q.variable) && suggestVariable(q.id, { force: true })}
                  placeholder={suggesting[q.id] ? "" : "e.g. budget_range"}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
                  {suggesting[q.id] ? (
                    <span className={styles.aiLoader} role="status">
                      <SparkleIcon className={styles.aiSparkle} />
                      <span className={styles.aiLoaderText}>AI is naming this answer</span>
                      <span className={styles.aiDots} aria-hidden="true">
                        <i />
                        <i />
                        <i />
                      </span>
                    </span>
                  ) : (
                    q.variable &&
                    !q.variableManual && (
                      <span className={styles.aiBadge} title="Suggested by AI from your question">
                        <SparkleIcon />
                      </span>
                    )
                  )}
                </span>
                {showErrors && isChecked && errors[q.id] && (
                  <span className={styles.variableError} role="alert">
                    {errors[q.id]}
                  </span>
                )}
                <span className={styles.variableHint}>
                  Filled in automatically from your question — you can change it. The lead&apos;s answer is saved
                  under this name after every call, so you can send it to your CRM (for example, a Monday.com
                  column) from Integrations.
                </span>
              </label>
            </div>
          );
        })}
      </div>

      {/* Add Custom Question Button / Form */}
      {!showAddForm ? (
        <button
          type="button"
          className={styles.addCustomBtn}
          onClick={() => setShowAddForm(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
          <span>Add Custom Question</span>
        </button>
      ) : (
        <div ref={addFormRef} className={styles.addCustomCard}>
          <h3 className={styles.addCustomTitle}>Add Custom Question</h3>

          <div className={styles.formGroup}>
            <input
              type="text"
              className={styles.formInput}
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Type your question here..."
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && customText.trim()) {
                  handleAddCustomQuestion();
                }
              }}
            />
          </div>

          <div className={styles.addFormActions}>
            <button
              type="button"
              className={styles.cancelAddBtn}
              onClick={() => {
                setShowAddForm(false);
                setCustomText("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.confirmAddBtn}
              disabled={!customText.trim()}
              onClick={handleAddCustomQuestion}
            >
              Add Question
            </button>
          </div>
        </div>
      )}

      {/* Anchor for smooth scroll */}
      <div ref={bottomAnchorRef} className={styles.bottomAnchor} />

      {/* Shared AnimatedButton. Wo apne aap position: fixed hota hai; yahan
          hamara .footer pehle se fixed hai, isliye use relative karke footer
          ke andar baithaya. bgSrc me leading slash: default path relative hai. */}
      <div className={styles.footer}>
        <div className={styles.continueWrap}>
          <AnimatedButton
            label={
              selectedCount > 0
                ? `Continue with ${selectedCount} Question${selectedCount > 1 ? "s" : ""}`
                : "Select at least 1 question"
            }
            onClick={handleNext}
            disabled={selectedCount === 0}
            isLoading={busy}
            positionType="relative"
            bottom="0px"
            width="100%"
            bgSrc="/svg/svg-theme2.svg"
          />
        </div>
      </div>
    </div>
  );
};

export default PounceQualifyingQuestions;
